import type { Command } from './command';

/** Undo / Redo の最大の手数（FR-EDT-007：最低 100 手）。 */
export const MAX_HISTORY = 100;

/** 履歴つきの状態。`state` が現在の状態で、`past`・`future` は操作の列（新しいものが後ろ）。 */
export interface History<S> {
  readonly state: S;
  /** 取り消せる操作（古い → 新しい） */
  readonly past: readonly Command<S>[];
  /** やり直せる操作（次にやり直す操作が末尾） */
  readonly future: readonly Command<S>[];
}

/** 履歴を空の状態から作る。 */
export function createHistory<S>(state: S): History<S> {
  return { state, past: [], future: [] };
}

/** 古い操作を捨てて、最大の手数に収める。 */
function trim<S>(past: readonly Command<S>[]): readonly Command<S>[] {
  return past.length > MAX_HISTORY ? past.slice(past.length - MAX_HISTORY) : past;
}

/** 操作を適用して履歴に積む。やり直せる操作は消える。 */
export function execute<S>(history: History<S>, command: Command<S>): History<S> {
  return {
    state: command.apply(history.state),
    past: trim([...history.past, command]),
    future: [],
  };
}

/**
 * すでに状態へ反映済みの操作を、履歴に記録する。
 * ドラッグのように、途中経過を `state` に直接反映しながら、終わった時点で 1 回の操作として積むときに使う。
 *
 * @param state 操作を反映した後の状態
 */
export function record<S>(history: History<S>, command: Command<S>, state: S): History<S> {
  return { state, past: trim([...history.past, command]), future: [] };
}

/** 状態だけを更新する（履歴は変えない）。ドラッグ中の途中経過の表示に使う。 */
export function preview<S>(history: History<S>, state: S): History<S> {
  return { ...history, state };
}

/** 直前の操作を取り消す。取り消せる操作がなければ、そのまま返す。 */
export function undo<S>(history: History<S>): History<S> {
  const command = history.past[history.past.length - 1];
  if (command === undefined) return history;
  return {
    state: command.revert(history.state),
    past: history.past.slice(0, -1),
    future: [...history.future, command],
  };
}

/** 取り消した操作をやり直す。やり直せる操作がなければ、そのまま返す。 */
export function redo<S>(history: History<S>): History<S> {
  const command = history.future[history.future.length - 1];
  if (command === undefined) return history;
  return {
    state: command.apply(history.state),
    past: trim([...history.past, command]),
    future: history.future.slice(0, -1),
  };
}

/** 取り消せる操作があるか。 */
export function canUndo<S>(history: History<S>): boolean {
  return history.past.length > 0;
}

/** やり直せる操作があるか。 */
export function canRedo<S>(history: History<S>): boolean {
  return history.future.length > 0;
}
